import React, { useState } from "react";
import { useApp } from "@/context/AppContext";
import { ccoreApi } from "@/lib/ccore";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Building2, Scale, User, Landmark, Briefcase, Check, Copy, ArrowRight, ArrowLeft, Mail } from "lucide-react";

const ORG_TYPES = [
  { code: "FIRM", label: "Law Firm", desc: "Partners, associates and staff", icon: Building2 },
  { code: "CHAMBER", label: "Advocate's Chamber", desc: "A lead advocate with juniors", icon: Scale },
  { code: "INDIVIDUAL", label: "Sole Practitioner", desc: "Just you, for now", icon: User },
  { code: "GOV_OFFICE", label: "Government Office", desc: "Govt pleader / law officer", icon: Landmark },
  { code: "LEGAL_DEPARTMENT", label: "Legal Department", desc: "In-house legal team", icon: Briefcase },
];
const STATES = ["Andhra Pradesh", "Telangana", "Karnataka", "Tamil Nadu", "Kerala", "Maharashtra", "Delhi", "Uttar Pradesh", "West Bengal", "Gujarat", "Rajasthan", "Madhya Pradesh", "Bihar", "Odisha", "Punjab", "Haryana", "Assam", "Jharkhand", "Chhattisgarh", "Uttarakhand", "Himachal Pradesh", "Goa", "Jammu & Kashmir", "Other"];

export function CreateOrgWizard({ open, onClose }) {
  const { createOrg, switchOrg } = useApp();
  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [orgType, setOrgType] = useState("");
  const [homeState, setHomeState] = useState("");
  const [primaryForum, setPrimaryForum] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [roles, setRoles] = useState([]);
  const [email, setEmail] = useState("");
  const [roleCode, setRoleCode] = useState("");
  const [sent, setSent] = useState([]);
  const [entering, setEntering] = useState(false);

  const reset = () => { setStep(1); setName(""); setOrgType(""); setHomeState(""); setPrimaryForum(""); setRes(null); setRoles([]); setEmail(""); setRoleCode(""); setSent([]); };
  const close = () => { if (!busy && !entering) { reset(); onClose(); } };

  const create = async () => {
    setBusy(true);
    try {
      const r = await createOrg({ name: name.trim(), orgType, homeState: homeState === "Other" ? "" : homeState, primaryForumId: primaryForum.trim() });
      setRes(r);
      const rl = await ccoreApi.orgRoles().catch(() => []);
      setRoles(rl.filter((x) => x.scope === "CORE" && x.code !== "OWNER"));
      setStep(3);
      toast.success("Organisation created. You are the owner.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create organisation");
    } finally { setBusy(false); }
  };

  const invite = async () => {
    if (!/^\S+@\S+\.\S+$/.test(email)) return toast.error("Enter a valid email");
    if (!roleCode) return toast.error("Choose a role");
    setBusy(true);
    try {
      const r = await ccoreApi.createInvite({ email: email.trim().toLowerCase(), roleCode });
      setSent((s) => [...s, { email: email.trim().toLowerCase(), roleCode, link: `${window.location.origin}/invite/${r.token}` }]);
      setEmail(""); setRoleCode("");
      toast.success("Invitation link generated.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create invitation"); }
    finally { setBusy(false); }
  };

  const enter = async () => {
    setEntering(true);
    try { await switchOrg(res.membershipId); } catch (e) { setEntering(false); toast.error("Created, but could not open the workspace. Please reload."); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-xl" data-testid="create-org-wizard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-2xl">
            <Building2 className="h-5 w-5 text-copper" /> {step < 3 ? "Create your organisation" : "Invite your team"}
          </DialogTitle>
          <DialogDescription>{step === 1 ? "Name it and tell us what kind of practice this is." : step === 2 ? "Set your home jurisdiction — we'll use it to pre-fill new matters." : "Send single-use links to colleagues, or skip and do this later."}</DialogDescription>
        </DialogHeader>

        <div className="mb-1 flex items-center gap-1.5">
          {[1, 2, 3].map((s) => <div key={s} className={cn("h-1.5 flex-1 rounded-full", step >= s ? "bg-copper" : "bg-line")} data-testid={`wizard-step-dot-${s}`} />)}
        </div>

        {step === 1 && (
          <div className="space-y-4">
            <div>
              <label className="text-xs font-medium text-ink-2">Organisation name</label>
              <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., Adusumilli & Associates" data-testid="wizard-org-name" />
            </div>
            <div>
              <label className="text-xs font-medium text-ink-2">Practice type</label>
              <div className="mt-1 grid grid-cols-1 gap-2 sm:grid-cols-2">
                {ORG_TYPES.map((t) => (
                  <button key={t.code} type="button" onClick={() => setOrgType(t.code)} data-testid={`wizard-org-type-${t.code}`}
                    className={cn("flex items-start gap-2 rounded-lg border p-2.5 text-left transition-colors", orgType === t.code ? "border-copper bg-copper-light/40" : "border-line hover:border-copper/40")}>
                    <t.icon className={cn("mt-0.5 h-4 w-4 shrink-0", orgType === t.code ? "text-copper" : "text-ink-2")} />
                    <div><div className="text-sm font-medium text-ink">{t.label}</div><div className="text-[11px] text-ink-muted">{t.desc}</div></div>
                  </button>
                ))}
              </div>
            </div>
            <div className="flex justify-end">
              <Button disabled={!name.trim() || !orgType} onClick={() => setStep(2)} data-testid="wizard-next-1">Continue <ArrowRight className="ml-1 h-4 w-4" /></Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div>
              <label className="text-xs font-medium text-ink-2">Home state</label>
              <Select value={homeState} onValueChange={setHomeState}>
                <SelectTrigger data-testid="wizard-home-state"><SelectValue placeholder="Select your primary state" /></SelectTrigger>
                <SelectContent className="max-h-72">{STATES.map((s) => <SelectItem key={s} value={s} data-testid={`wizard-state-${s}`}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-ink-2">Primary forum <span className="text-ink-muted">(optional)</span></label>
              <Input value={primaryForum} onChange={(e) => setPrimaryForum(e.target.value)} placeholder="e.g., High Court of Telangana" data-testid="wizard-primary-forum" />
            </div>
            <div className="flex items-center justify-between">
              <Button variant="ghost" onClick={() => setStep(1)} data-testid="wizard-back-2"><ArrowLeft className="mr-1 h-4 w-4" /> Back</Button>
              <Button disabled={busy} onClick={create} data-testid="wizard-create">{busy ? "Creating…" : "Create organisation"}</Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <div className="rounded-lg border border-success/30 bg-success/5 p-2.5 text-sm text-ink-2" data-testid="wizard-created-note"><Check className="mr-1 inline h-4 w-4 text-success" /> <b className="text-ink">{name.trim()}</b> is ready.</div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
              <div className="flex-1"><label className="text-xs font-medium text-ink-2">Colleague's email</label><Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="colleague@example.com" data-testid="wizard-invite-email" /></div>
              <div className="w-full sm:w-44"><label className="text-xs font-medium text-ink-2">Role</label>
                <Select value={roleCode} onValueChange={setRoleCode}><SelectTrigger data-testid="wizard-invite-role"><SelectValue placeholder="Role" /></SelectTrigger>
                  <SelectContent>{roles.map((r) => <SelectItem key={r.code} value={r.code} data-testid={`wizard-invite-role-${r.code}`}>{r.label}</SelectItem>)}</SelectContent></Select>
              </div>
              <Button variant="outline" disabled={busy} onClick={invite} data-testid="wizard-invite-add"><Mail className="mr-1 h-4 w-4" /> Generate link</Button>
            </div>
            {sent.length > 0 && (
              <div className="space-y-1.5" data-testid="wizard-invites-sent">
                {sent.map((s, i) => (
                  <div key={i} className="flex items-center gap-2 rounded-lg border border-line bg-card p-2 text-xs">
                    <span className="font-medium text-ink">{s.email}</span><span className="text-ink-muted">· {s.roleCode}</span>
                    <code className="ml-auto max-w-[180px] truncate text-ink-2">{s.link}</code>
                    <Button size="sm" variant="ghost" className="h-6" onClick={() => { navigator.clipboard.writeText(s.link); toast.success("Link copied"); }} data-testid={`wizard-invite-copy-${i}`}><Copy className="h-3.5 w-3.5" /></Button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-center justify-end gap-2">
              <Button disabled={entering} onClick={enter} data-testid="wizard-enter-workspace">{entering ? "Opening…" : sent.length ? "Enter workspace" : "Skip & enter workspace"} <ArrowRight className="ml-1 h-4 w-4" /></Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
